import {
  Box,
  Button,
  ButtonProps,
  Center,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import { TVariables as MapBranchesVariables, useMapBranchesApi } from 'apis/csv-uploads/use-map-branches-api';
import { TVariables as MapItemsVariables, useMapItemsApi } from 'apis/csv-uploads/use-map-items-api';
import { TVariables as MapSuppliersVariables, useMapSuppliersApi } from 'apis/csv-uploads/use-map-suppliers-api';
import { FileIcon } from 'icons/file-icon';
import { useEffect, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

interface IFile extends File {
  preview: string;
}

interface IUploadCSVButton extends ButtonProps {
  templateType: 'branches' | 'warehouses' | 'suppliers' | 'items' | 'warehouse_items';
}

export const UploadCSVButton = ({ templateType, ...props }: IUploadCSVButton) => {
  const [file, setFile] = useState<IFile | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isLoading: isMappingBranchesLoading,
    mutate: mapBranchesApi,
    isSuccess: isMapBranchesSuccess,
  } = useMapBranchesApi();
  const {
    isLoading: isMappingSuppliersLoading,
    mutate: mapSuppliersApi,
    isSuccess: isMapSuppliersSuccess,
  } = useMapSuppliersApi();
  const { isLoading: isMappingItemsLoading, mutate: mapItemsApi, isSuccess: isMapItemsSuccess } = useMapItemsApi();

  const isLoading = isMappingBranchesLoading || isMappingSuppliersLoading || isMappingItemsLoading;
  const isSuccess = isMapBranchesSuccess || isMapSuppliersSuccess || isMapItemsSuccess;

  const dropZoneOptions: DropzoneOptions = {
    maxFiles: 1,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    },
    onDropAccepted: (acceptedFiles) => {
      const _file: File = acceptedFiles[0];

      Object.assign(_file, {
        preview: URL.createObjectURL(_file),
      });

      setFile(_file as IFile);
    },
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone(dropZoneOptions);

  const onDelete = () => {
    setFile(null);
  };

  const _onClose = () => {
    onClose();
    onDelete();
  };

  const onConfirm = () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const variables: MapBranchesVariables | MapSuppliersVariables | MapItemsVariables = {
      body: formData,
    };

    switch (templateType) {
      case 'branches':
        mapBranchesApi(variables);
        break;

      case 'suppliers':
        mapSuppliersApi(variables);
        break;

      case 'items':
        mapItemsApi(variables);
        break;

      default:
        break;
    }
  };

  // Close modal when API call succeed
  useEffect(() => {
    if (isSuccess) _onClose();
  }, [isSuccess]);

  // Make sure to revoke the data uri to avoid memory leaks, will run on unmount
  useEffect(() => {
    return () => {
      if (file && file.preview) URL.revokeObjectURL(file.preview);
    };
  }, []);

  return (
    <Box>
      <Button colorScheme='red' variant='solid' onClick={onOpen} {...props}>
        Upload Template
      </Button>

      <Modal size='lg' variant='secondary' isOpen={isOpen} onClose={_onClose} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text color='red.500' fontWeight={'semibold'} fontSize='lg'>
              Upload Bulk Mapping files
            </Text>
            <Text color='gray.500' fontWeight={'normal'} fontSize='sm'>
              Upload the
              <Text as='span' color='red.500' mx='xs'>
                .csv
              </Text>
              files.
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py='2xl'>
            {/* Drag & drop */}
            {!file && (
              <Center flexDir={'column'} _hover={{ cursor: 'pointer' }} {...getRootProps()}>
                <input {...getInputProps()} />

                <Icon width='451px' height='203px' viewBox='0 0 451 203' fill='none'>
                  <path
                    d='M432.072 1.5H17.9279C8.5789 1.5 1 10.2908 1 21.1347V181.865C1 192.709 8.5789 201.5 17.9279 201.5H432.072C441.421 201.5 449 192.709 449 181.865V21.1347C449 10.2908 441.421 1.5 432.072 1.5Z'
                    stroke='#D6D6D6'
                    strokeWidth='3'
                    strokeDasharray='26 26'
                  />
                  <path
                    d='M254 53H197M250.833 93.6957L225.5 71.7826L200.167 93.6957M225.5 125V71.7826'
                    stroke='#C5C5C5'
                    strokeWidth='7.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M108.478 165C113.054 165 116.134 161.986 116.134 157.652C116.134 153.362 113.054 150.326 108.456 150.326H103V165H108.478ZM108.456 162.734H105.574V152.592H108.478C111.734 152.592 113.494 154.836 113.494 157.652C113.494 160.446 111.646 162.734 108.456 162.734ZM120.863 165V157.762C121.325 156.992 122.623 156.332 123.613 156.332C123.899 156.332 124.119 156.354 124.317 156.398V154.11C122.953 154.11 121.677 154.902 120.863 155.914V154.374H118.553V165H120.863ZM135.002 165V157.938C135.002 155.1 132.934 154.11 130.558 154.11C128.864 154.11 127.324 154.638 126.092 155.826L127.06 157.432C127.962 156.508 129.018 156.068 130.206 156.068C131.658 156.068 132.692 156.816 132.692 158.048V159.632C131.9 158.73 130.69 158.268 129.238 158.268C127.5 158.268 125.564 159.28 125.564 161.744C125.564 164.076 127.522 165.264 129.238 165.264C130.646 165.264 131.9 164.758 132.692 163.834V165H135.002ZM130.162 163.68C128.864 163.68 127.874 162.932 127.874 161.766C127.874 160.622 128.864 159.852 130.162 159.852C131.152 159.852 132.142 160.226 132.692 160.952V162.58C132.142 163.306 131.152 163.68 130.162 163.68ZM142.331 169.312C144.927 169.312 147.787 168.278 147.787 164.45V154.374H145.477V155.848C144.619 154.704 143.409 154.11 142.067 154.11C139.317 154.11 137.359 156.112 137.359 159.566C137.359 163.064 139.361 165.022 142.067 165.022C143.453 165.022 144.663 164.318 145.477 163.24V164.45C145.477 166.716 143.849 167.42 142.331 167.42C140.923 167.42 139.823 167.046 138.943 166.056L137.865 167.728C139.185 168.916 140.527 169.312 142.331 169.312ZM142.749 162.954C140.901 162.954 139.734 161.612 139.734 159.566C139.734 157.52 140.901 156.156 142.749 156.156C143.827 156.156 144.949 156.794 145.477 157.564V161.546C144.949 162.316 143.827 162.954 142.749 162.954ZM168.772 165C167.716 163.988 166.792 163.042 165.912 162.118C166.77 160.952 167.43 159.654 167.936 158.466L166.066 157.608C165.802 158.466 165.318 159.632 164.614 160.688C164.13 160.16 163.646 159.588 163.162 158.994C162.744 158.51 162.216 157.872 161.71 157.168C163.536 156.288 165.362 155.32 165.362 153.098C165.362 151.184 163.734 150.106 161.754 150.106C159.466 150.106 157.772 151.602 157.772 153.56C157.772 154.572 158.19 155.694 158.806 156.794C157.112 157.652 155.616 158.818 155.616 161.084C155.616 163.856 157.728 165.264 160.324 165.264C161.996 165.264 163.338 164.648 164.438 163.724C164.922 164.23 165.384 164.67 165.736 165H168.772ZM160.632 163.416C159.268 163.416 158.058 162.492 158.058 160.886C158.058 159.654 158.762 158.884 159.73 158.246C160.236 158.972 160.808 159.698 161.358 160.336C161.732 160.82 162.414 161.59 163.162 162.382C162.414 162.998 161.578 163.416 160.632 163.416ZM160.082 153.626C160.082 152.548 160.874 151.8 161.842 151.8C162.678 151.8 163.316 152.328 163.316 153.23C163.316 154.462 162.194 155.166 160.808 155.826C160.368 155.056 160.082 154.286 160.082 153.626ZM181.847 165C186.423 165 189.503 161.986 189.503 157.652C189.503 153.362 186.423 150.326 181.825 150.326H176.369V165H181.847ZM181.825 162.734H178.943V152.592H181.847C185.103 152.592 186.863 154.836 186.863 157.652C186.863 160.446 185.015 162.734 181.825 162.734ZM194.232 165V157.762C194.694 156.992 195.992 156.332 196.982 156.332C197.268 156.332 197.488 156.354 197.686 156.398V154.11C196.322 154.11 195.046 154.902 194.232 155.914V154.374H191.922V165H194.232ZM204.367 165.264C207.755 165.264 209.823 162.712 209.823 159.676C209.823 156.618 207.755 154.11 204.367 154.11C200.979 154.11 198.911 156.618 198.911 159.676C198.911 162.712 200.979 165.264 204.367 165.264ZM204.367 163.218C202.409 163.218 201.309 161.568 201.309 159.676C201.309 157.806 202.409 156.156 204.367 156.156C206.347 156.156 207.425 157.806 207.425 159.676C207.425 161.568 206.347 163.218 204.367 163.218ZM217.902 165.264C220.652 165.264 222.61 163.174 222.61 159.676C222.61 156.178 220.652 154.11 217.902 154.11C216.516 154.11 215.284 154.77 214.492 155.826V154.374H212.182V169.048H214.492V163.526C215.372 164.648 216.56 165.264 217.902 165.264ZM217.22 163.218C216.142 163.218 214.998 162.558 214.492 161.766V157.564C215.02 156.772 216.142 156.156 217.22 156.156C219.046 156.156 220.234 157.608 220.234 159.676C220.234 161.744 219.046 163.218 217.22 163.218ZM235.412 165.264C238.8 165.264 240.868 162.712 240.868 159.676C240.868 156.618 238.8 154.11 235.412 154.11C232.024 154.11 229.956 156.618 229.956 159.676C229.956 162.712 232.024 165.264 235.412 165.264ZM235.412 163.218C233.454 163.218 232.354 161.568 232.354 159.676C232.354 157.806 233.454 156.156 235.412 156.156C237.392 156.156 238.47 157.806 238.47 159.676C238.47 161.568 237.392 163.218 235.412 163.218ZM245.537 165V157.762C245.999 156.992 247.297 156.332 248.287 156.332C248.573 156.332 248.793 156.354 248.991 156.398V154.11C247.627 154.11 246.351 154.902 245.537 155.914V154.374H243.227V165H245.537Z'
                    fill='#C5C5C5'
                  />
                  <path
                    d='M261.366 165.264C263.456 165.264 264.688 164.362 265.436 163.372L263.918 161.942C263.324 162.778 262.51 163.218 261.476 163.218C259.562 163.218 258.264 161.744 258.264 159.676C258.264 157.608 259.562 156.156 261.476 156.156C262.51 156.156 263.324 156.552 263.918 157.388L265.436 156.002C264.688 154.99 263.456 154.11 261.366 154.11C258.154 154.11 255.888 156.464 255.888 159.676C255.888 162.888 258.154 165.264 261.366 165.264ZM269.793 165V150.326H267.483V165H269.793ZM273.955 153.076C274.747 153.076 275.385 152.438 275.385 151.646C275.385 150.854 274.747 150.216 273.955 150.216C273.163 150.216 272.525 150.854 272.525 151.646C272.525 152.438 273.163 153.076 273.955 153.076ZM275.099 165V154.374H272.789V165H275.099ZM282.936 165.264C285.026 165.264 286.258 164.362 287.006 163.372L285.488 161.942C284.894 162.778 284.08 163.218 283.046 163.218C281.132 163.218 279.834 161.744 279.834 159.676C279.834 157.608 281.132 156.156 283.046 156.156C284.08 156.156 284.894 156.552 285.488 157.388L287.006 156.002C286.258 154.99 285.026 154.11 282.936 154.11C279.724 154.11 277.458 156.464 277.458 159.676C277.458 162.888 279.724 165.264 282.936 165.264ZM298.997 165L294.509 159.192L298.909 154.374H296.049L291.363 159.5V150.326H289.053V165H291.363V162.14L292.859 160.6L296.093 165H298.997ZM315.831 165V157.476C315.831 155.298 314.687 154.11 312.399 154.11C310.727 154.11 309.363 154.968 308.659 155.804V150.326H306.349V165H308.659V157.564C309.187 156.86 310.199 156.156 311.387 156.156C312.685 156.156 313.521 156.64 313.521 158.312V165H315.831ZM323.758 165.264C325.43 165.264 326.992 164.714 328.07 163.702L327.014 162.184C326.244 162.954 325.034 163.372 323.978 163.372C321.976 163.372 320.788 162.03 320.612 160.446H328.796V159.874C328.796 156.53 326.772 154.11 323.582 154.11C320.436 154.11 318.192 156.596 318.192 159.676C318.192 163.042 320.546 165.264 323.758 165.264ZM326.574 158.796H320.59C320.678 157.52 321.602 156.002 323.56 156.002C325.65 156.002 326.508 157.542 326.574 158.796ZM333.387 165V157.762C333.849 156.992 335.147 156.332 336.137 156.332C336.423 156.332 336.643 156.354 336.841 156.398V154.11C335.477 154.11 334.201 154.902 333.387 155.914V154.374H331.077V165H333.387ZM343.632 165.264C345.304 165.264 346.866 164.714 347.944 163.702L346.888 162.184C346.118 162.954 344.908 163.372 343.852 163.372C341.85 163.372 340.662 162.03 340.486 160.446H348.67V159.874C348.67 156.53 346.646 154.11 343.456 154.11C340.31 154.11 338.066 156.596 338.066 159.676C338.066 163.042 340.42 165.264 343.632 165.264ZM346.448 158.796H340.464C340.552 157.52 341.476 156.002 343.434 156.002C345.524 156.002 346.382 157.542 346.448 158.796Z'
                    fill='#E53E51'
                  />
                </Icon>

                {/* Errors */}
                {!!fileRejections.length && (
                  <Text color='red.500' mt='sm'>
                    {fileRejections[0]?.errors[0]?.message}
                  </Text>
                )}
              </Center>
            )}

            {/* Preview */}
            {file && (
              <Center flexDir={'column'}>
                <FileIcon boxSize={'80px'} />

                <Text color='green.500' mt='sm' textAlign={'center'}>
                  {file.name}
                </Text>

                <Button variant={'link'} colorScheme='red' mt='md' onClick={onDelete}>
                  Delete
                </Button>
              </Center>
            )}
          </ModalBody>

          {file && (
            <ModalFooter>
              <HStack spacing='lg'>
                <Button variant={'link'} colorScheme={'gray'} onClick={_onClose}>
                  Cancel
                </Button>
                <Button variant={'solid'} colorScheme={'red'} onClick={onConfirm} isLoading={isLoading}>
                  Confirm
                </Button>
              </HStack>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};
